import { useEffect, useMemo, useState } from 'react'

import { useQuery } from 'react-query'

import {
    Table,
    Row,
    Col,
    Form,
    Card,
    Spinner,
    Badge
} from 'react-bootstrap'

import api from '../components/api'

import { rotuloEnviado } from '../components/functions'


export default function ModalDesafios(props) {

    const {
        adesao_institucional_id,
        nome_fantasia
    } = props


    const anoAtual = new Date().getFullYear()

    // Começa sempre no ano atual - 1
    const [ano, setAno] = useState(anoAtual - 1)

    // Controla os percentuais utilizados pelas barras
    const [percentuaisAnimados, setPercentuaisAnimados] = useState({})


    /*
    =========================================================
    SITUAÇÕES
    =========================================================
    */

    const situacoes = [
        {
            campo: 'naoIniciado',
            nome: 'Não iniciado',
            cor: '#adb5bd'
        },
        {
            campo: 'preenchimento',
            nome: 'Em preenchimento',
            cor: '#6c757d'
        },
        {
            campo: 'preenchido',
            nome: 'Preenchido',
            cor: '#7edeed'
        },
        {
            campo: 'corrigido',
            nome: 'Corrigido',
            cor: '#91edd9'
        },
        {
            campo: 'pendencias',
            nome: 'Com pendências',
            cor: '#ffc4ab'
        },
        {
            campo: 'completo',
            nome: 'Completo',
            cor: '#59d970'
        },
        {
            campo: 'verificado',
            nome: 'Verificado',
            cor: '#f5c6f5'
        }
    ]


    /*
    =========================================================
    COR DA SITUAÇÃO ATUAL
    =========================================================
    */

    const corSituacao = (situacao) => {

        switch (situacao) {

            case 'I':
                return '#6c757d'

            case 'C':
                return '#7edeed'

            case 'O':
                return '#91edd9'

            case 'E':
                return '#ffc4ab'

            case 'V':
                return '#59d970'

            case 'Z':
                return '#f5c6f5'

            case 'P':
                return '#ffc4ab'

            case 'A':
                return '#ebe7b2'

            case null:
            case undefined:
                return '#adb5bd'

            default:
                return '#adb5bd'
        }

    }


    /*
    =========================================================
    STATUS ATUAL
    =========================================================
    */

    const {
        data: statusAtual
    } = useQuery({

        queryKey: [
            'statusParticipacao',
            adesao_institucional_id
        ],

        queryFn: async () => {

            const result = await api.get(
                `statusParticipacao/${adesao_institucional_id}`
            )

            return result.data ?? {}

        },

        enabled: !!adesao_institucional_id

    })


    /*
    =========================================================
    DASHBOARD DOS DESAFIOS
    =========================================================
    */

    const {
        data: dadosDesafios,
        isLoading: loadingDesafios
    } = useQuery({

        queryKey: [
            'statusDesafios',
            adesao_institucional_id,
            ano
        ],

        queryFn: async () => {

            const result = await api.post(
                'statusDesafios',
                {
                    adesao_institucional_id,
                    ano
                }
            )

            return result.data ?? []

        },

        enabled:
            !!adesao_institucional_id &&
            !!ano

    })


    /*
    =========================================================
    FUNÇÃO PARA ANIMAR AS BARRAS
    =========================================================
    */

    const animarBarras = () => {

        // Primeiro coloca tudo em zero
        setPercentuaisAnimados({})


        // Aguarda o navegador renderizar o estado zerado
        setTimeout(() => {

            if (
                !dadosDesafios ||
                dadosDesafios.length === 0
            ) {
                return
            }


            const novosPercentuais = {}


            dadosDesafios.forEach(desafio => {

                situacoes.forEach(situacao => {

                    const percentual =
                        Number(
                            desafio[situacao.campo] || 0
                        )


                    if (percentual > 0) {

                        const chave =
                            `${desafio.desafio}-${situacao.campo}`


                        novosPercentuais[chave] =
                            percentual

                    }

                })

            })


            setPercentuaisAnimados(
                novosPercentuais
            )

        }, 150)

    }


    /*
    =========================================================
    ANIMAÇÃO QUANDO OS DADOS / ANO MUDAM
    =========================================================
    */

    useEffect(() => {

        if (
            !dadosDesafios ||
            dadosDesafios.length === 0
        ) {

            setPercentuaisAnimados({})

            return

        }


        const timer = setTimeout(() => {

            const novosPercentuais = {}


            dadosDesafios.forEach(desafio => {

                situacoes.forEach(situacao => {

                    const percentual =
                        Number(
                            desafio[situacao.campo] || 0
                        )


                    if (percentual > 0) {

                        const chave =
                            `${desafio.desafio}-${situacao.campo}`


                        novosPercentuais[chave] =
                            percentual

                    }

                })

            })


            setPercentuaisAnimados(
                novosPercentuais
            )

        }, 150)


        return () => {
            clearTimeout(timer)
        }

    }, [dadosDesafios, ano])


    /*
    =========================================================
    ANIMAÇÃO AO ABRIR O MODAL
    =========================================================
    */

    useEffect(() => {

        const modal =
            document.getElementById(
                'modalDesafios'
            )


        if (!modal) {
            return
        }


        const aoAbrirModal = () => {

            animarBarras()

        }


        // Bootstrap/jQuery
        if (typeof window.$ !== 'undefined') {

            window.$(modal).on(
                'shown.bs.modal',
                aoAbrirModal
            )

        }


        return () => {

            if (
                typeof window.$ !== 'undefined'
            ) {

                window.$(modal).off(
                    'shown.bs.modal',
                    aoAbrirModal
                )

            }

        }

    }, [dadosDesafios])


    /*
    =========================================================
    ANOS
    =========================================================
    */

    const anos = useMemo(() => {

        const lista = []


        for (
            let i = anoAtual;
            i >= anoAtual - 5;
            i--
        ) {

            lista.push(i)

        }


        return lista

    }, [anoAtual])


    /*
    =========================================================
    COMPONENTE DE SITUAÇÃO
    =========================================================
    */

    const SituacaoBadge = ({
        situacao
    }) => {

        const cor =
            corSituacao(situacao)


        const texto =
            situacao
                ? rotuloEnviado(situacao)
                : 'Não iniciado'


        return (

            <span
                style={{
                    display: 'inline-block',
                    background: cor,
                    padding: '5px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#333'
                }}
            >
                {texto}
            </span>

        )

    }


    /*
    =========================================================
    RENDER
    =========================================================
    */

    return (

        <>

            <div
                className="modal fade"
                id="modalDesafios"
                tabIndex="-1"
                role="dialog"
            >

                <div
                    className="modal-dialog modal-xl"
                    role="document"
                    style={{
                        width: '95%',
                        maxWidth: 1200
                    }}
                >

                    <div
                        className="modal-content"
                        style={{
                            width: '100%'
                        }}
                    >


                        {/* =================================================
                            HEADER
                        ================================================= */}

                        <div className="modal-header">

                            <h1 className="modal-title">

                                {nome_fantasia}

                            </h1>


                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label="Close"
                            >

                                <span aria-hidden="true">
                                    &times;
                                </span>

                            </button>

                        </div>


                        <div
                            className="modal-body"
                            style={{
                                width: '100%',
                                padding: '20px'
                            }}
                        >


                            {/* =================================================
                                SITUAÇÃO ATUAL
                            ================================================= */}

                            <Card
                                className="border-0 shadow-sm mb-4"
                                style={{
                                    width: '100%',
                                    marginLeft: 0,
                                    marginRight: 0
                                }}
                            >

                                <Card.Body>

                                    <div
                                        className="d-flex align-items-center mb-3"
                                    >

                                        <div>

                                            <h4 className="mb-0">
                                                Situação atual
                                            </h4>

                                            <small className="text-muted">
                                                Última participação registrada
                                            </small>

                                        </div>

                                    </div>


                                    <Table
                                        striped
                                        bordered
                                        hover
                                        responsive
                                        size="sm"
                                        className="mb-0"
                                    >

                                        <thead>

                                            <tr>

                                                <th>
                                                    Desafio
                                                </th>

                                                <th
                                                    style={{
                                                        textAlign:
                                                            'center'
                                                    }}
                                                >
                                                    Último ano
                                                </th>

                                                <th>
                                                    Situação
                                                </th>

                                            </tr>

                                        </thead>


                                        <tbody>


                                            {/* CLIMA */}

                                            <tr>

                                                <td>
                                                    Clima
                                                </td>

                                                <td
                                                    style={{
                                                        textAlign:
                                                            'center'
                                                    }}
                                                >
                                                    {
                                                        statusAtual?.ultimoAnoClima ||
                                                        '-'
                                                    }
                                                </td>

                                                <td>

                                                    <SituacaoBadge
                                                        situacao={
                                                            statusAtual?.ultimaSituacaoClima
                                                        }
                                                    />

                                                </td>

                                            </tr>


                                            {/* RESÍDUOS */}

                                            <tr>

                                                <td>
                                                    Resíduos
                                                </td>

                                                <td
                                                    style={{
                                                        textAlign:
                                                            'center'
                                                    }}
                                                >
                                                    {
                                                        statusAtual?.ultimoAnoResiduos ||
                                                        '-'
                                                    }
                                                </td>

                                                <td>

                                                    <SituacaoBadge
                                                        situacao={
                                                            statusAtual?.ultimaSituacaoResiduos
                                                        }
                                                    />

                                                </td>

                                            </tr>


                                            {/* ENERGIA */}

                                            <tr>

                                                <td>
                                                    Energia
                                                </td>

                                                <td
                                                    style={{
                                                        textAlign:
                                                            'center'
                                                    }}
                                                >
                                                    {
                                                        statusAtual?.ultimoAnoEnergia ||
                                                        '-'
                                                    }
                                                </td>

                                                <td>

                                                    <SituacaoBadge
                                                        situacao={
                                                            statusAtual?.ultimaSituacaoEnergia
                                                        }
                                                    />

                                                </td>

                                            </tr>


                                            {/* COMPRAS */}

                                            <tr>

                                                <td>
                                                    Compras
                                                </td>

                                                <td
                                                    style={{
                                                        textAlign:
                                                            'center'
                                                    }}
                                                >
                                                    {
                                                        statusAtual?.ultimoAnoCompras ||
                                                        '-'
                                                    }
                                                </td>

                                                <td>

                                                    <SituacaoBadge
                                                        situacao={
                                                            statusAtual?.ultimaSituacaoCompras
                                                        }
                                                    />

                                                </td>

                                            </tr>


                                        </tbody>

                                    </Table>

                                </Card.Body>

                            </Card>



                            {/* =================================================
                                DASHBOARD
                            ================================================= */}

                            <Card
                                className="border-0 shadow-sm"
                                style={{
                                    width: '100%',
                                    marginLeft: 0,
                                    marginRight: 0
                                }}
                            >

                                <Card.Body>


                                    {/* CABEÇALHO */}

                                    <Row
                                        className="align-items-end mb-4"
                                    >

                                        <Col>

                                            <h4 className="mb-1">
                                                Desempenho dos desafios
                                            </h4>

                                        </Col>


                                        <Col
                                            xs={12}
                                            md={3}
                                            className="mt-3 mt-md-0"
                                        >

                                            <Form.Group>

                                                <Form.Control
                                                    as="select"
                                                    value={ano}
                                                    onChange={(e) => {

                                                        setAno(
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )

                                                    }}
                                                >

                                                    {
                                                        anos.map(item => (

                                                            <option
                                                                key={item}
                                                                value={item}
                                                            >
                                                                {item}
                                                            </option>

                                                        ))
                                                    }

                                                </Form.Control>

                                            </Form.Group>

                                        </Col>

                                    </Row>



                                    {/* =================================================
                                        LOADING
                                    ================================================= */}

                                    {

                                        loadingDesafios

                                            ?

                                            <div
                                                className="text-center py-5"
                                            >

                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                />

                                                <div
                                                    className="mt-3 text-muted"
                                                >
                                                    Carregando dashboard...
                                                </div>

                                            </div>


                                            :


                                            <>


                                                {/* =================================================
                                                    DESAFIOS
                                                ================================================= */}

                                                {

                                                    dadosDesafios &&
                                                    dadosDesafios.length > 0


                                                        ?


                                                        dadosDesafios.map(
                                                            desafio => (

                                                                <Card
                                                                    key={
                                                                        `${desafio.desafio}-${desafio.ano}`
                                                                    }
                                                                    className="border-0 mb-4"
                                                                    style={{
                                                                        background:
                                                                            '#f8f9fa',

                                                                        borderRadius:
                                                                            14,

                                                                        width:
                                                                            '100%',

                                                                        marginLeft:
                                                                            0,

                                                                        marginRight:
                                                                            0
                                                                    }}
                                                                >

                                                                    <Card.Body>


                                                                        {/* TÍTULO */}

                                                                        <div
                                                                            className="d-flex justify-content-between align-items-center mb-4"
                                                                        >

                                                                            <div>

                                                                                <h3
                                                                                    className="font-weight-bold mb-1"
                                                                                >
                                                                                    {
                                                                                        desafio.desafio
                                                                                    }
                                                                                </h3>

                                                                            </div>

                                                                        </div>



                                                                        {/* =================================================
                                                                            BARRAS
                                                                        ================================================= */}

                                                                        {

                                                                            situacoes.map(
                                                                                situacao => {

                                                                                    const percentual =
                                                                                        Number(
                                                                                            desafio[
                                                                                                situacao.campo
                                                                                            ] || 0
                                                                                        )


                                                                                    /*
                                                                                    Não exibe
                                                                                    situações
                                                                                    com 0%
                                                                                    */

                                                                                    if (
                                                                                        percentual <= 0
                                                                                    ) {

                                                                                        return null

                                                                                    }


                                                                                    const chave =
                                                                                        `${desafio.desafio}-${situacao.campo}`


                                                                                    const percentualAnimado =
                                                                                        Number(
                                                                                            percentuaisAnimados[
                                                                                                chave
                                                                                            ] || 0
                                                                                        )


                                                                                    return (

                                                                                        <div
                                                                                            key={
                                                                                                chave
                                                                                            }
                                                                                            className="mb-3"
                                                                                        >


                                                                                            {/* LABEL */}

                                                                                            <div
                                                                                                className="d-flex justify-content-between align-items-center mb-1"
                                                                                            >

                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            13,

                                                                                                        fontWeight:
                                                                                                            600
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        situacao.nome
                                                                                                    }
                                                                                                </span>

                                                                                            </div>



                                                                                            {/* BARRA */}

                                                                                            <div
                                                                                                style={{
                                                                                                    height:
                                                                                                        25,

                                                                                                    width:
                                                                                                        '100%',

                                                                                                    background:
                                                                                                        '#e9ecef',

                                                                                                    borderRadius:
                                                                                                        15,

                                                                                                    overflow:
                                                                                                        'hidden'
                                                                                                }}
                                                                                            >

                                                                                                <div
                                                                                                    style={{
                                                                                                        width:
                                                                                                            `${percentualAnimado}%`,

                                                                                                        height:
                                                                                                            '100%',

                                                                                                        background:
                                                                                                            situacao.cor,

                                                                                                        borderRadius:
                                                                                                            15,

                                                                                                        transition:
                                                                                                            'width 1.4s cubic-bezier(0.4, 0, 0.2, 1)',

                                                                                                        willChange:
                                                                                                            'width',

                                                                                                        display:
                                                                                                            'flex',

                                                                                                        alignItems:
                                                                                                            'center',

                                                                                                        justifyContent:
                                                                                                            'flex-end',

                                                                                                        padding:
                                                                                                            '0 10px',

                                                                                                        fontSize:
                                                                                                            11,

                                                                                                        fontWeight:
                                                                                                            700,

                                                                                                        color:
                                                                                                            '#333',

                                                                                                        boxSizing:
                                                                                                            'border-box'
                                                                                                    }}
                                                                                                >

                                                                                                    {

                                                                                                        percentualAnimado >= 10

                                                                                                            ?

                                                                                                            `${percentualAnimado.toFixed(1)}%`

                                                                                                            :

                                                                                                            ''

                                                                                                    }

                                                                                                </div>

                                                                                            </div>

                                                                                        </div>

                                                                                    )

                                                                                }
                                                                            )

                                                                        }


                                                                    </Card.Body>

                                                                </Card>

                                                            )
                                                        )


                                                        :


                                                        <div
                                                            className="text-center py-5 text-muted"
                                                        >

                                                            <div
                                                                style={{
                                                                    fontSize: 40
                                                                }}
                                                            >
                                                                📊
                                                            </div>

                                                            <div
                                                                className="mt-2"
                                                            >
                                                                Não existem dados
                                                                para o ano
                                                                selecionado.
                                                            </div>

                                                        </div>

                                                }



                                                {/* =================================================
                                                    LEGENDA
                                                ================================================= */}

                                                <Card
                                                    className="border-0"
                                                    style={{
                                                        background:
                                                            '#f8f9fa',

                                                        width:
                                                            '100%',

                                                        marginLeft:
                                                            0,

                                                        marginRight:
                                                            0
                                                    }}
                                                >

                                                    <Card.Body>

                                                        <div
                                                            className="font-weight-bold mb-3"
                                                        >
                                                            Legenda
                                                        </div>


                                                        <Row>

                                                            {

                                                                situacoes.map(
                                                                    situacao => (

                                                                        <Col
                                                                            key={
                                                                                situacao.campo
                                                                            }
                                                                            xs={6}
                                                                            sm={4}
                                                                            md={3}
                                                                            className="mb-2"
                                                                        >

                                                                            <div
                                                                                className="d-flex align-items-center"
                                                                            >

                                                                                <span
                                                                                    style={{
                                                                                        width:
                                                                                            15,

                                                                                        height:
                                                                                            15,

                                                                                        background:
                                                                                            situacao.cor,

                                                                                        borderRadius:
                                                                                            4,

                                                                                        marginRight:
                                                                                            7,

                                                                                        display:
                                                                                            'inline-block'
                                                                                    }}
                                                                                />

                                                                                <small>
                                                                                    {
                                                                                        situacao.nome
                                                                                    }
                                                                                </small>

                                                                            </div>

                                                                        </Col>

                                                                    )
                                                                )

                                                            }

                                                        </Row>

                                                    </Card.Body>

                                                </Card>


                                            </>

                                    }

                                </Card.Body>

                            </Card>


                        </div>



                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <div className="modal-footer">

                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-dismiss="modal"
                            >
                                Fechar
                            </button>

                        </div>


                    </div>

                </div>

            </div>

        </>

    )

}